/**
 * 供給力変化画面 — 純計算ヘルパー (design JS の集計ロジックを移植)
 *
 * MW = capacityKw / 1000。 当日 = 約定日 (受渡日 − 1)、 翌日 = 受渡日。
 * 価格圧力: 純変化 (= 停止終了MW − 停止開始MW) >= 0 → 下落圧力 / < 0 → 上昇圧力。
 */
import type { SupplyCapacityItem } from '~/constants/supplyCapacityChange'
// 整形は `formatDateValue` ('YYYY-MM-DD')。 ページ固有の表記 (fmtMMDD/fmtBadge/fmtMw) のみ局所。
import { addDays, isSameDay } from '~/models/dateTime'

const WK = ['日', '月', '火', '水', '木', '金', '土']

/** 翌日 (= 受渡日の既定値) のローカル日付 */
export const tomorrow = (now: Date): Date => addDays(now, 1)

/** 受渡日 (delivery) から 約定日 today / 受渡日 tmr を導出 */
export const getDates = (delivery: Date): { today: Date; tmr: Date } => ({
  today: addDays(delivery, -1),
  tmr: delivery,
})

export const fmtMMDD = (d: Date): string => {
  const m = d.getMonth() + 1
  const dt = d.getDate()
  return `${m < 10 ? '0' : ''}${m}/${dt < 10 ? '0' : ''}${dt}`
}

/** 'MM/DD (曜)' */
export const fmtBadge = (d: Date): string => `${fmtMMDD(d)} (${WK[d.getDay()]})`

/** 符号つき・千区切り MW 文字列 (例: +1,200 / -600) */
export const fmtMw = (v: number): string =>
  `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString()}`

export type Pressure = {
  /** 価格下落圧力か (= 供給回復で価格が下がる方向)。 false なら上昇圧力。 */
  isFalling: boolean
  /** 表示色のトーン。 positive = 下落圧力 (緑) / negative = 上昇圧力 (赤)。 */
  tone: 'positive' | 'negative'
}

/**
 * 純変化 MW から価格圧力を判定する単一の真実。
 * net >= 0 → 供給回復 → 価格下落圧力 (positive / 下矢印)、 net < 0 → 上昇圧力 (negative / 上矢印)。
 * 符号反転ロジックを各 component に手コピーせず、 ここに集約する。
 */
export const pressureOf = (net: number): Pressure =>
  net >= 0
    ? { isFalling: true, tone: 'positive' }
    : { isFalling: false, tone: 'negative' }

/** item の MW (capacityKw → MW) */
export const mwOf = (it: SupplyCapacityItem): number => it.capacityKw / 1000

const sumMw = (arr: SupplyCapacityItem[]): number =>
  arr.reduce((s, x) => s + mwOf(x), 0)

/** 指定日の停止開始 (isStart=true) / 停止終了 (false) item を抽出 */
export const itemsOnDay = (
  items: SupplyCapacityItem[],
  date: Date,
  isStart: boolean,
): SupplyCapacityItem[] =>
  items.filter((it) => isSameDay(isStart ? it.start : it.end, date))

const itemsByArea = (
  items: SupplyCapacityItem[],
  date: Date,
  isStart: boolean,
  area: string,
): SupplyCapacityItem[] =>
  itemsOnDay(items, date, isStart).filter((it) => it.area === area)

export type ChartDatum = { category: string } & Record<string, number | string>

export type ChartModel = {
  data: ChartDatum[]
  activeAreas: string[]
  todayNet: number
  tmrNet: number
}

/**
 * stacked BarChart 用データ (カテゴリ = 当日 / 翌日、 系列 = エリア純変化)。
 * 当日/翌日 の見出しは純関数で t() を呼べないため、 呼び出し側で翻訳済み文字列を
 * labels で注入する (i18n のハードコード禁止に従う)。
 */
export const buildChartModel = (
  items: SupplyCapacityItem[],
  today: Date,
  tmr: Date,
  areaOrder: readonly string[],
  labels: { today: string; tmr: string },
): ChartModel => {
  const todayLabel = `${labels.today} ${fmtMMDD(today)}`
  const tmrLabel = `${labels.tmr} ${fmtMMDD(tmr)}`
  const todayRow: ChartDatum = { category: todayLabel }
  const tmrRow: ChartDatum = { category: tmrLabel }
  const activeAreas: string[] = []
  for (const area of areaOrder) {
    const tNet =
      sumMw(itemsByArea(items, today, false, area)) -
      sumMw(itemsByArea(items, today, true, area))
    const mNet =
      sumMw(itemsByArea(items, tmr, false, area)) -
      sumMw(itemsByArea(items, tmr, true, area))
    todayRow[area] = tNet
    tmrRow[area] = mNet
    if (tNet !== 0 || mNet !== 0) activeAreas.push(area)
  }
  const todayNet = activeAreas.reduce(
    (s, a) => s + (Number(todayRow[a]) || 0),
    0,
  )
  const tmrNet = activeAreas.reduce((s, a) => s + (Number(tmrRow[a]) || 0), 0)
  return { data: [todayRow, tmrRow], activeAreas, todayNet, tmrNet }
}

export type CardModel = { startMw: number; endMw: number; net: number }

/** インパクトカード 1 日分 (停止開始MW / 停止終了MW / 純変化) */
export const buildCardModel = (
  items: SupplyCapacityItem[],
  date: Date,
): CardModel => {
  const startMw = sumMw(itemsOnDay(items, date, true))
  const endMw = sumMw(itemsOnDay(items, date, false))
  return { startMw, endMw, net: endMw - startMw }
}

export type AreaRow = {
  item: SupplyCapacityItem
  dayKind: 'today' | 'tmr'
  kind: 'start' | 'end'
}

export type AreaBlock = {
  area: string
  netMw: number
  rows: AreaRow[]
}

/** エリア別ブロック (工事のあるエリアのみ。 行順 = 当日開始/当日終了/翌日開始/翌日終了) */
export const buildAreaBlocks = (
  items: SupplyCapacityItem[],
  today: Date,
  tmr: Date,
  areaOrder: readonly string[],
): AreaBlock[] => {
  const blocks: AreaBlock[] = []
  for (const area of areaOrder) {
    const tStart = itemsByArea(items, today, true, area)
    const tEnd = itemsByArea(items, today, false, area)
    const mStart = itemsByArea(items, tmr, true, area)
    const mEnd = itemsByArea(items, tmr, false, area)
    if (!tStart.length && !tEnd.length && !mStart.length && !mEnd.length) {
      continue
    }
    const netMw = sumMw(tEnd) + sumMw(mEnd) - sumMw(tStart) - sumMw(mStart)
    const rows: AreaRow[] = [
      ...tStart.map(
        (item): AreaRow => ({ item, dayKind: 'today', kind: 'start' }),
      ),
      ...tEnd.map((item): AreaRow => ({ item, dayKind: 'today', kind: 'end' })),
      ...mStart.map(
        (item): AreaRow => ({ item, dayKind: 'tmr', kind: 'start' }),
      ),
      ...mEnd.map((item): AreaRow => ({ item, dayKind: 'tmr', kind: 'end' })),
    ]
    blocks.push({ area, netMw, rows })
  }
  return blocks
}
